import {
  publicCrmAmountLabel,
  publicCrmPrice,
  type PublicCrmPricing,
} from "./publicCrmPricing";

interface MarketingPricingSectionProps {
  pricing: PublicCrmPricing | null;
  loading: boolean;
  failed: boolean;
}

export function MarketingPricingSection({
  pricing,
  loading,
  failed,
}: MarketingPricingSectionProps) {
  const offer = pricing?.plan.founding_offer;
  const foundingPrice = Boolean(offer?.available);
  const activePrices = foundingPrice ? offer?.prices || [] : pricing?.plan.prices || [];
  const monthly = publicCrmPrice(activePrices, "MONTHLY");
  const annual = publicCrmPrice(activePrices, "ANNUAL");
  const listMonthly = publicCrmPrice(pricing?.plan.prices || [], "MONTHLY");
  const seats = pricing?.plan.base_included_seats;

  return (
    <section className="pm-pricing" id="pricing" aria-labelledby="pm-pricing-title">
      <header className="pm-section-heading pm-pricing-heading">
        <span>LeadCat CRM Pricing</span>
        <h2 id="pm-pricing-title">One complete plan for managing social enquiries</h2>
        <p>Start with the full LeadCat CRM workflow—channels, ownership, follow-ups and conversion visibility in one workspace.</p>
      </header>

      {loading ? (
        <div className="pm-pricing-loading" role="status" aria-label="Loading current LeadCat CRM pricing">
          <span /><span /><span />
        </div>
      ) : failed || !pricing || !monthly ? (
        <article className="pm-pricing-unavailable">
          <span>Current pricing</span>
          <h3>Let us confirm the right LeadCat CRM offer for you.</h3>
          <p>Pricing could not be loaded right now. Contact our team for the current plan and activation terms.</p>
          <a className="pm-button pm-button-primary" href="mailto:director@staminal.in?subject=LeadCat%20CRM%20pricing">
            Ask about pricing <span aria-hidden="true">→</span>
          </a>
        </article>
      ) : (
        <div className="pm-pricing-layout">
          <article className="pm-pricing-card">
            <div className="pm-pricing-card-head">
              <div>
                <span className="pm-pricing-badge">{foundingPrice ? "Founding price" : "LeadCat CRM"}</span>
                <h3>{pricing.plan.name}</h3>
                <p>Complete social CRM for a team that wants every enquiry to have an owner and a next step.</p>
              </div>
              <div className="pm-price-block">
                {foundingPrice && listMonthly ? <del>{publicCrmAmountLabel(listMonthly)}</del> : null}
                <div><strong>{publicCrmAmountLabel(monthly)}</strong><span>/ month</span></div>
                {annual ? <small>or {publicCrmAmountLabel(annual)} / year</small> : null}
              </div>
            </div>

            <div className="pm-pricing-features" aria-label="Social Media CRM plan includes">
              <span><i aria-hidden="true">✓</i>{seats} users included</span>
              <span><i aria-hidden="true">✓</i>Instagram, Facebook and WhatsApp integrations</span>
              <span><i aria-hidden="true">✓</i>Lead assignment and follow-up tracking</span>
              <span><i aria-hidden="true">✓</i>Sales pipeline and revenue visibility</span>
              <span><i aria-hidden="true">✓</i>Quotations and payment tracking</span>
              <span><i aria-hidden="true">✓</i>Reports and team visibility</span>
            </div>

            <div className="pm-pricing-actions">
              <a className="pm-button pm-button-primary" href="/react/signup">
                {foundingPrice ? "Get Founding Price" : "Get Started"} <span aria-hidden="true">→</span>
              </a>
              <small>Review the current billing and activation terms before payment.</small>
            </div>
          </article>

          <aside className="pm-pricing-faq" aria-label="LeadCat CRM pricing questions">
            <h3>Good to know</h3>
            <details>
              <summary>What does the plan include?</summary>
              <p>The customer channels, lead pipeline, assignment, follow-ups, quotations, revenue tracking and reporting shown above.</p>
            </details>
            <details>
              <summary>Is LeadCat Billing included?</summary>
              <p>No. LeadCat Billing is a separate focused billing product with its own pricing.</p>
            </details>
            <details>
              <summary>Are provider charges included?</summary>
              <p>Charges billed separately by Meta, Razorpay or another connected provider are not included when applicable.</p>
            </details>
          </aside>
        </div>
      )}
    </section>
  );
}

